import Cookies from 'js-cookie'
import Vue from 'vue'
import { levenshteinDistance } from '~/lib/helpers/string'
import { useDomains } from '~/lib/hooks'
import {
  REQUIRED_FIELDS,
  US_ENTITY_TYPE_ENUMS,
  CA_ENTITY_TYPES_NOT_ORGANIZATIONAL,
} from '@/lib/constants/domain-schema'
import {
  getEnumOptions,
  caLanguageEnumsRef,
  getUsEntityTypeEnumOptions,
} from '@/lib/helpers/domain-schema-fields'
import { LogoTracker, LTEvents } from '~/lib/events/tracker'

function buildPurchaseDto(
  {
    registrantInfo,
    domain,
    period,
    agreementKeys,
    privacy = true,
    autoRenew = true,
    usIntent,
    usEntityType,
    caEntityType,
    caLanguage,
    citizenship,
  },
  schema,
  tld
) {
  const address = {
    address1: registrantInfo.address.line1,
    address2: registrantInfo.address.line2,
    city: registrantInfo.address.city,
    country: registrantInfo.address.country,
    state: registrantInfo.address.state,
    postalCode: registrantInfo.address.postal,
  }

  const contact = {
    addressMailing: address,
    email: registrantInfo.email,
    nameFirst: registrantInfo.given_name,
    nameLast: registrantInfo.family_name,
    phone: registrantInfo.phone,
  }

  const isUsIntentPersonal = usIntent === 'PERSONAL'
  const isCaEntityTypeOrganizational = !CA_ENTITY_TYPES_NOT_ORGANIZATIONAL.includes(
    caEntityType
  )

  switch (tld) {
    case 'us':
      if (!isUsIntentPersonal) {
        contact.organization = registrantInfo.usOrganization
      }

      break

    case 'ca':
      if (isCaEntityTypeOrganizational) {
        contact.organization = registrantInfo.caOrganization
      }

      break

    default:
      break
  }

  const purchaseDto = {
    consent: {
      agreedAt: new Date().toISOString(),
      // This must be an IP address
      agreedBy: registrantInfo.given_name,
      agreementKeys: agreementKeys,
    },
    contactAdmin: contact,
    contactBilling: contact,
    contactRegistrant: contact,
    contactTech: contact,
    domain,
    period,
    renewAuto: autoRenew,
  }

  const isPrivacyAOption = schema?.properties?.privacy ? true : false

  if (isPrivacyAOption) {
    purchaseDto.privacy = privacy
  }

  const isEntityTypeAOption = schema?.properties?.entityType ? true : false
  const isIntentAOption = schema?.properties?.intent ? true : false
  const isCitizenshipAOption = schema?.properties?.citizenship ? true : false
  const isLanguageAOption = schema?.properties?.language ? true : false

  switch (tld) {
    case 'us':
      if (isIntentAOption) {
        purchaseDto.intent = usIntent
      }

      if (isEntityTypeAOption) {
        purchaseDto.entityType = usEntityType
      }

      if (isCitizenshipAOption && usIntent !== 'PERSONAL') {
        purchaseDto.citizenship = citizenship
      }

      break

    case 'ca':
      if (isEntityTypeAOption) {
        purchaseDto.entityType = caEntityType
      }

      if (isLanguageAOption) {
        purchaseDto.language = caLanguage
      }

      break

    default:
      break
  }

  return purchaseDto
}

const sortDomainByPopularity = popularTlds => (a, b) => {
  const aIndex = popularTlds.indexOf(`.${a.domain.split('.')[1]}`)
  const bIndex = popularTlds.indexOf(`.${b.tld.split('.')[1]}`)

  if (aIndex === -1 && bIndex === -1) {
    return 0
  }

  if (aIndex === -1) {
    return 1
  }

  if (bIndex === -1) {
    return -1
  }

  return aIndex - bIndex
}

const sortDomainByPriceAsc = (a, b) => a.price - b.price
const sortDomainByPriceDesc = (a, b) => b.price - a.price
const sortDomainByLengthAsc = (a, b) => a.domain.length - b.domain.length

const excludeUnselectedTlds = selectedTlds => item => {
  const tld = `.${item.domain.split('.')[1]}`

  return selectedTlds.includes(tld)
}

const sortDomainByRelevance = original => (a, b) => {
  const aDistance = levenshteinDistance(original, a.domain.split('.')[0])
  const bDistance = levenshteinDistance(original, b.domain.split('.')[0])

  return aDistance - bDistance
}

export const state = () => ({
  domains: [],

  showUnavailable: false,
  noQueryPopularTLDDomainsAvailable: true,

  pagination: {
    hasMore: true,
    limit: 50,
  },

  loading: true,
  query: null,
  sortOptionHandle: 'popularity',
  selectedDomains: [],
  results: [],
  resultsTlds: [],
  domainTldFilter: '',
  agreementsAndSchema: {},
  agreementContents: [],
  extraSchemaRequiredFields: {},

  /**
   * This will be sent to the server under cart_item.options.domain_purchase_metadata
   */
  registrantInfo: {
    given_name: '',
    family_name: '',
    email: '',
    // Important: Must be in form /^\+([0-9]){1,3}\.([0-9]\ ?){5,14}$/
    phone: '',
    organization: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      // Must be the 2 letter ISO code
      country: '',
      state: '',
      postal: '',
    },
  },

  tldsToShow: [
    { selected: true, value: '.com' },
    { selected: true, value: '.org' },
    { selected: true, value: '.net' },
    { selected: true, value: '.shop' },
    { selected: true, value: '.link' },
    { selected: true, value: '.store' },
    { selected: true, value: '.online' },
    { selected: true, value: '.site' },
    { selected: true, value: '.io' },
    { selected: true, value: '.co' },
    { selected: true, value: '.ca' },
    { selected: true, value: '.us' },
  ],

  popularTlds: ['.com', '.link', '.org', '.shop', '.store'],
})

export const mutations = {
  setDomains(state, domains) {
    state.domains = domains
  },

  setDomainTldFilter(state, filter) {
    state.domainTldFilter = filter
  },

  setSortOptionHandle(state, handle) {
    state.sortOptionHandle = handle
  },

  setTldSelected(state, { tld, selected }) {
    const tldToShow = state.tldsToShow.find(t => t.value === tld)
    Vue.set(tldToShow, 'selected', selected)
  },

  setAllTldsSelected(state, selected) {
    state.tldsToShow.forEach(tld => {
      Vue.set(tld, 'selected', selected)
    })
  },

  setAgreementsAndSchema(state, payload) {
    state.agreementsAndSchema[payload.key] = payload.value
  },

  clearAgreementsAndSchema(state) {
    state.agreementsAndSchema = {}
  },

  setAgreementContents(state, agreement) {
    state.agreementContents.push(agreement)
  },

  clearAgreementContents(state) {
    state.agreementContents = []
  },

  setExtraSchemaRequiredFields(state, payload) {
    if (state.extraSchemaRequiredFields[payload.key] === undefined) {
      state.extraSchemaRequiredFields[payload.key] = [payload.value]
    } else if (
      !state.extraSchemaRequiredFields[payload.key].includes(payload.value)
    ) {
      state.extraSchemaRequiredFields[payload.key] = [
        ...state.extraSchemaRequiredFields[payload.key],
        payload.value,
      ]
    }
  },

  clearExtraSchemaRequiredFields(state) {
    state.extraSchemaRequiredFields = {}
  },

  setLoading(state, loading) {
    state.loading = loading
  },

  setQuery(state, query) {
    state.query = query
  },

  setResults(state, results) {
    state.results = results.map(item => formatDomain(item))
  },

  setResultTlds(state, tlds) {
    state.resultsTlds = tlds
  },

  updateResults(state, results) {
    // TODO: We don't want to accidentally show domains with a period different than 1.
    // Our billing system doesn't support it yet.
    results.forEach(item => {
      if (item.period !== undefined && item.period !== 1) {
        item.available = false
      }
    })

    state.results = state.results.map(item => {
      // Try and find the full domain result
      const fullDomain = results.find(result => result.domain === item.domain)

      // If we have a full domain, format it and return it instead of the current version
      if (fullDomain) {
        return {
          ...formatDomain(fullDomain),
          loaded: true,
        }
      }

      // If not, just return the current version
      return item
    }, [])
  },

  setSelectedDomains(state, selectedDomains) {
    state.selectedDomains = selectedDomains
  },

  setRegistrantInfo(state, registrantInfo) {
    state.registrantInfo = registrantInfo
  },

  setNoQueryPopularTLDDomainsAvailable(
    state,
    noQueryPopularTLDDomainsAvailable
  ) {
    state.noQueryPopularTLDDomainsAvailable = noQueryPopularTLDDomainsAvailable
  },
}

export const getters = {
  domainIsPopular: state => domain => {
    return state.popularTlds.includes(domain.tld)
  },

  /**
   * Takes a TLD and attempts to return a domain for it.  The current
   * `query` will be used, which will be pre-processed to remove special
   * characters and spaces.
   *
   * The first matching available domain will be returned.  If there is no available
   * domain that matches, the first unavailable domain will be returned.
   *
   * If no domains are present in the results at all, a fallback domain will be returned,
   * set to unavailable.
   */
  exactMatchDomainQueryForTld: (state, getters) => tld => {
    if (!state.query) {
      return null
    }

    const cleanedTld = tld.replace('.', '')
    const cleanedQuery = state.query
      .replace(/[^a-zA-Z0-9\-]/g, '')
      .toLowerCase()

    const findMatch = result => {
      const [name, resultTld] = result.domain.split('.')

      if (cleanedTld !== resultTld) {
        return false
      }

      if (name === cleanedQuery) {
        return true
      }

      return false
    }

    // We're going to check the searchResults first, which will be pre-filtered.
    // If we don't find a match, we can check the full list.  Any domains returned there will be unavailable.
    const matchingTld =
      getters.searchResults.find(findMatch) || state.results.find(findMatch)

    if (matchingTld) {
      return matchingTld
    }

    return {
      value: cleanedQuery,
      tld,
      price: undefined,
      available: false,
    }
  },

  searchResults: state => {
    let results = [...state.results]

    const allTldsSelected = state.tldsToShow.every(tld => tld.selected)

    if (!allTldsSelected) {
      const selectedTlds = state.tldsToShow
        .filter(tld => tld.selected)
        .map(tld => tld.value)

      results = results.filter(excludeUnselectedTlds(selectedTlds))
    }

    if (state.sortOptionHandle === 'relevance') {
      results = results.sort(sortDomainByRelevance(state.query))
    }

    if (state.sortOptionHandle === 'price') {
      results = results.sort(sortDomainByPriceAsc)
    }

    if (state.sortOptionHandle === 'price-desc') {
      results = results.sort(sortDomainByPriceDesc)
    }

    if (state.sortOptionHandle === 'length') {
      results = results.sort(sortDomainByLengthAsc)
    }

    if (state.sortOptionHandle === 'popularity') {
      results = results.sort(sortDomainByPopularity(state.popularTlds))
    }

    if (state.sortOptionHandle === 'exact-match') {
      results = results.filter(
        result => result.value.toLowerCase() === state.query.toLowerCase()
      )
    }

    if (!state.showUnavailable) {
      results = results.filter(item => item.available)
    }

    results = results.filter(result => result.definitive)

    return results
  },

  getPackageForTld: (_state, _getters, rootState) => tld => {
    return rootState.app.catalog.find(({ code }) => code === 'domain')
  },

  domainIsSelected: state => ({ domain }) => {
    return state.selectedDomains.some(item => item.domain === domain)
  },

  domainCartItems: (state, getters) => ({
    usIntent,
    usEntityType,
    caEntityType,
    caLanguage,
    citizenship,
  }) => {
    return state.selectedDomains.map(domain => {
      const pkg = getters.getPackageForTld(domain.tld)

      const agreementKeys = getters.getAgreementKeysForADomain(domain)

      const tld = domain.tld.replace('.', '')

      const purchaseDto = buildPurchaseDto(
        {
          registrantInfo: state.registrantInfo,
          domain: domain.domain,
          agreementKeys,
          period: 1,
          privacy: true,
          autoRenew: true,
          usIntent,
          usEntityType,
          caEntityType,
          caLanguage,
          citizenship,
        },
        state.agreementsAndSchema[tld].schema,
        tld
      )

      return {
        item: { id: pkg.code },
        price: pkg.prices[0],
        package: pkg,
        options: {
          domain_purchase_metadata: purchaseDto,
        },
      }
    })
  },

  getAgreementKeysForADomain: state => domain => {
    let agreementKeys

    const tld = domain.tld.replace('.', '')

    if (tld === 'us' || tld === 'ca') {
      agreementKeys = state.agreementsAndSchema[tld]?.agreements
        .filter(agreement => agreement.agreementKey !== 'DNPA')
        .map(agreement => agreement.agreementKey)
    } else {
      agreementKeys = state.agreementsAndSchema[tld]?.agreements.map(
        agreement => agreement.agreementKey
      )
    }

    return agreementKeys
  },

  getExtraSchemaContent: state => {
    const allExtraSchemaContent = {}

    for (const [tld, fields] of Object.entries(
      state.extraSchemaRequiredFields
    )) {
      switch (tld) {
        case 'us':
          fields.forEach(field => {
            if (field.hasEnum) {
              const options = getEnumOptions(
                state.agreementsAndSchema,
                tld,
                field.field
              )

              if (allExtraSchemaContent[tld] === undefined) {
                allExtraSchemaContent[tld] = {
                  [field.field]: options,
                }
              } else {
                allExtraSchemaContent[tld] = {
                  ...allExtraSchemaContent[tld],
                  [field.field]: options,
                }
              }
            } else if (field.field === 'entityType') {
              const personalOptions = getUsEntityTypeEnumOptions(
                'PERSONAL',
                US_ENTITY_TYPE_ENUMS
              )
              const businessOptions = getUsEntityTypeEnumOptions(
                'BUSINESS',
                US_ENTITY_TYPE_ENUMS
              )

              if (allExtraSchemaContent[tld] === undefined) {
                allExtraSchemaContent[tld] = {
                  [field.field]: {
                    PERSONAL: personalOptions,
                    BUSINESS: businessOptions,
                  },
                }
              } else {
                allExtraSchemaContent[tld] = {
                  ...allExtraSchemaContent[tld],
                  [field.field]: {
                    PERSONAL: personalOptions,
                    BUSINESS: businessOptions,
                  },
                }
              }
            }
          })

          break

        case 'ca':
          fields.forEach(field => {
            if (field.hasEnum) {
              let options

              if (field.field === 'language') {
                options = getEnumOptions(
                  state.agreementsAndSchema,
                  tld,
                  field.field,
                  caLanguageEnumsRef
                )
              } else {
                options = getEnumOptions(
                  state.agreementsAndSchema,
                  tld,
                  field.field
                )
              }

              if (allExtraSchemaContent[tld] === undefined) {
                allExtraSchemaContent[tld] = {
                  [field.field]: options,
                }
              } else {
                allExtraSchemaContent[tld] = {
                  ...allExtraSchemaContent[tld],
                  [field.field]: options,
                }
              }
            }
          })

          break

        default:
          break
      }
    }

    return allExtraSchemaContent
  },
}

export const actions = {
  async search({ state, commit, getters, dispatch }, { query, country }) {
    commit('setLoading', true)
    commit('setQuery', query)
    commit('setResults', [])

    const tlds = state.tldsToShow.map(item => item.value.replace('.', ''))

    // First we ask the API for a list of domains...
    try {
      const { data } = await this.$api.get(`/api/v3/domains/suggest`, {
        params: {
          limit: state.pagination.limit,
          tlds,
          waitMs: 1000,
          query,
          country,
        },
      })

      // Commit the initial results so the user can see the list while we fetch some more info
      commit('setResults', data)
    } catch (error) {
      this.$sentry.captureException(error)

      commit('setLoading', false)

      // handle this in the component
      throw error
    }

    // Then we ask the API for more details on the returned domains...
    await dispatch('fetchDomainsAvailability')

    dispatch('fetchResultsTlds')

    for (const tld of state.popularTlds) {
      const match = getters.exactMatchDomainQueryForTld(tld)

      if (match.available) {
        commit('setDomainTldFilter', tld)

        commit('setNoQueryPopularTLDDomainsAvailable', false)

        break
      }
    }

    LogoTracker.track(LTEvents.DOMAINS_SEARCHED, {
      query,
      tlds,
    })

    commit('setLoading', false)
  },

  async fetchDomainsAvailability({ commit, state }) {
    const domainsToUpdate = new Set()

    for (const result of state.results) {
      domainsToUpdate.add(result.domain)
    }

    for (const domain of state.selectedDomains) {
      domainsToUpdate.add(domain.domain)
    }

    const domains = Array.from(domainsToUpdate)

    if (state.results.length === 0) {
      return
    }

    try {
      const { data } = await this.$api.post(`/api/v3/domains/available`, {
        checkType: 'FULL',
        domains,
        currency: Cookies.get('currency') || undefined,
      })

      commit('updateResults', data.domains)
    } catch (error) {
      this.$sentry.captureException(error)

      // handle this in the component
      throw error
    }

    if (state.selectedDomains.length > 0) {
      // We also need to update
      const selectedDomains = state.selectedDomains
        .map(item =>
          state.results.find(result => result.domain === item.domain)
        )
        .filter(domain => domain !== null)

      commit('setSelectedDomains', selectedDomains)
    }
  },

  async fetchAgreementsAndSchemaForSelectedDomains({ commit, state }) {
    const domains = useDomains(this.$api)

    const existingTlds = []
    const existingAgreementKeys = []

    commit('clearAgreementsAndSchema')
    commit('clearAgreementContents')
    commit('clearExtraSchemaRequiredFields')

    const promises = state.selectedDomains.map(async domain => {
      const tld = domain.tld.replace('.', '')

      if (!existingTlds.includes(tld)) {
        existingTlds.push(tld)

        const hasPrivacy = domain?.privacy ? domain.privacy : false

        const [agreementsResult, schemaResult] = await Promise.all([
          domains.listAgreements({
            tlds: [tld],
            privacy: hasPrivacy,
          }),
          domains.fetchTldSchema(tld),
        ])

        const agreements = agreementsResult.agreements
        const schema = schemaResult.data

        // Populate the extra schema required fields
        schema.required.forEach(field => {
          if (!REQUIRED_FIELDS.includes(field)) {
            const hasEnum = schema.properties[field]?.enum ? true : false

            const payload = { key: tld, value: { field, hasEnum } }

            commit('setExtraSchemaRequiredFields', payload)
          }
        })

        // Populate the agreement HTML contents
        agreements.forEach(agreement => {
          if (!existingAgreementKeys.includes(agreement.agreementKey)) {
            existingAgreementKeys.push(agreement.agreementKey)

            commit('setAgreementContents', agreement.content)
          }
        })

        // Populate the agreements and schema object
        const payload = {
          key: tld,
          value: {
            agreements,
            schema,
          },
        }

        commit('setAgreementsAndSchema', payload)
      }
    })

    await Promise.all(promises)
  },

  async submitRegistrantInformation(
    { commit, state, getters },
    {
      domains,
      privacy = true,
      period = 1,
      registrantInfo,
      usIntent,
      usEntityType,
      caEntityType,
      caLanguage,
      citizenship,
    }
  ) {
    const promises = domains.map(async domain => {
      const tld = domain.tld.replace('.', '')

      const agreementKeys = getters.getAgreementKeysForADomain(domain)

      const schema = state.agreementsAndSchema[tld].schema

      const purchaseDto = buildPurchaseDto(
        {
          registrantInfo,
          agreementKeys,
          domain: domain.domain,
          period,
          privacy,
          autoRenew: true,
          usIntent,
          usEntityType,
          caEntityType,
          caLanguage,
          citizenship,
        },
        schema,
        tld
      )

      try {
        await this.$api.post(`/api/v3/domains/purchase/validate`, purchaseDto)
      } catch (e) {
        const error = e.response?.data

        throw error || e
      }
    })

    await Promise.all(promises)

    commit('setRegistrantInfo', registrantInfo)
  },

  fetchResultsTlds({ commit, state }) {
    const resultTlds = []

    state.results.forEach(result => {
      const isExistingTld = resultTlds.includes(result.tld)

      const isAvailableAndDefinitive = result.available && result.definitive

      if (!isExistingTld && isAvailableAndDefinitive)
        resultTlds.push(result.tld)
    })

    commit('setResultTlds', resultTlds)
  },

  /**
   * Check if a domain has automatic forwarding
   */
  async retrieveForwardingRules(context, id) {
    try {
      const { data } = await this.$api.get(`/api/v3/domains/${id}/forwarding`)
      return data
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },

  /**
   * Add automatic forwarding to www subdomain
   * Used for have logo flow
   */
  async addForwardingRule(context, { id, body }) {
    try {
      await this.$api.post(`/api/v3/domains/${id}/forwarding`, body)
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },

  /**
   * Remove automatic forwarding
   */
  async removeForwardingRules(context, id) {
    try {
      await this.$api.delete(`/api/v3/domains/${id}/forwarding`)
    } catch (error) {
      this.$sentry.captureException(error)
    }
  },
}

function formatDomain(item) {
  const [value, ...rest] = item.domain.split('.')

  return {
    ...item,
    value,
    tld: `.${rest.join('.')}`,
  }
}
